import React, { useState } from "react";
import RangeSlider from "../range-slider";
import SelectBox from "./form/select-box";
import { useTranslation } from "next-i18next";
import Tooltip from "./tooltip";

/**
 * Free Forever allowances, shown as their own $0 block above the billed usage.
 * Mirrors pricing.cards[0].features in common.json — keep the two in step.
 */
const FREE_TIER = {
  dataVolume: 100, // GB
  rumSessions: 1000,
  syntheticChecks: 20000,
  browserTestRuns: 10,
};

const PricingCalculator = () => {
  const { t } = useTranslation('common');
  const calculatorData = t('pricing.calculator', { returnObjects: true, defaultValue: {} }) as any;
  // Start every slider at its free-tier allowance, so the calculator opens on
  // what Free Forever actually covers and totals $0 until you exceed it.
  const [dataVolume, setDataVolume] = useState(FREE_TIER.dataVolume);
  const [rumSessions, setRumSessions] = useState(FREE_TIER.rumSessions);
  const [syntheticChecks, setSyntheticChecks] = useState(FREE_TIER.syntheticChecks);
  const [browserTestRuns, setBrowserTestRuns] = useState(FREE_TIER.browserTestRuns);
  const [opsAiIssues, setOpsAiIssues] = useState(0);
  const [memoryType, setMemoryType] = useState("GB");

  const dataVolumeMax = 100 * 1024;
  const rumSessionsMax = 10000000;
  const syntheticChecksMax = 10000000;
  const browserTestRunsMax = 100000;
  const opsAiIssuesMax = 100000;

  const rates = {
    dataVolume: 0.3, // $0.3 per GB
    rumSessions: 1, // $1 per 1K sessions
    syntheticChecks: 1, // $1 per 5K checks
    browserTestRuns: 9.99, // $9.99 per 1K test runs
  };

  const rateUnits = {
    dataVolume: 1, // per GB
    rumSessions: 1000, // per 1K sessions
    syntheticChecks: 5000, // per 5K checks
    browserTestRuns: 1000, // per 1K test runs
  };

  // Average token usage per OpsAI issue fix
  const tokensPerIssue = {
    input: 80_000, // 80K
    output: 11_000, // 11K
    cache: 70_000, // 70K
  };

  // Anthropic rates per 1M tokens
  const opsAiRatesPerMillion = {
    input: 3,
    output: 15,
    cache: 6,
  };

  const MILLION = 1_000_000;

  const costPerIssue = {
    input: (tokensPerIssue.input / MILLION) * opsAiRatesPerMillion.input,
    output: (tokensPerIssue.output / MILLION) * opsAiRatesPerMillion.output,
    cache: (tokensPerIssue.cache / MILLION) * opsAiRatesPerMillion.cache,
  };

  const getDataVolumeInGB = () => {
    if (memoryType === "TB") return dataVolume * 1000;
    if (memoryType === "PB") return dataVolume * 1000 * 1000;
    return dataVolume;
  };

  /** Billable volume is computed in GB; show it in whichever unit reads best. */
  const formatGigabytes = (gb: number) => {
    if (gb >= 1_000_000) return `${+(gb / 1_000_000).toFixed(2)}PB`;
    if (gb >= 1000) return `${+(gb / 1000).toFixed(2)}TB`;
    return `${+gb.toFixed(2)}GB`;
  };

  const formatDataVolumeDisplay = () => {
    if (memoryType === "GB") return `${dataVolume}GB`;
    if (memoryType === "TB") return `${dataVolume}TB`;
    return `${dataVolume}PB`;
  };

  const dataVolumeGB = getDataVolumeInGB();

  // Only usage above the Free Forever allowance is billed. Browser test runs and
  // OpsAI tokens have no allowance here and are billed from the first unit.
  const billableDataVolumeGB = Math.max(0, dataVolumeGB - FREE_TIER.dataVolume);
  const billableRumSessions = Math.max(0, rumSessions - FREE_TIER.rumSessions);
  const billableSyntheticChecks = Math.max(
      0,
      syntheticChecks - FREE_TIER.syntheticChecks
  );
  const billableBrowserTestRuns = Math.max(
      0,
      browserTestRuns - FREE_TIER.browserTestRuns
  );

  const dataVolumeCost =
      (billableDataVolumeGB / rateUnits.dataVolume) * rates.dataVolume;
  const rumSessionsCost =
      (billableRumSessions / rateUnits.rumSessions) * rates.rumSessions;
  const syntheticChecksCost =
      (billableSyntheticChecks / rateUnits.syntheticChecks) * rates.syntheticChecks;
  const browserTestRunsCost =
      (billableBrowserTestRuns / rateUnits.browserTestRuns) * rates.browserTestRuns;

  const anthropicBreakup = {
    input: opsAiIssues * costPerIssue.input,
    output: opsAiIssues * costPerIssue.output,
    cache: opsAiIssues * costPerIssue.cache,
  };

  const issueTokenBreakup = {
    input: opsAiIssues * (tokensPerIssue.input / 1000), // in K
    output: opsAiIssues * (tokensPerIssue.output / 1000),
    cache: opsAiIssues * (tokensPerIssue.cache / 1000),
  };

  const issueTokenTotal =
      issueTokenBreakup.input + issueTokenBreakup.output + issueTokenBreakup.cache;

  const formatTokenAmount = (valueInK: number) => {
    if (valueInK === 0) return "0";
    if (valueInK > 999) {
      const valueInM = valueInK / 1000;
      const formatted = Number.isInteger(valueInM)
        ? valueInM
        : parseFloat(valueInM.toFixed(2));
      return `${formatted}M`;
    }
    return `${valueInK}K`;
  };

  const opsAiAnthropicCost =
      anthropicBreakup.input + anthropicBreakup.output + anthropicBreakup.cache;

  /**
   * Free-tier rows to display. An allowance for something the visitor has not
   * entered any usage for is just noise, so each row appears only once its
   * input is above zero — and the whole block hides when none of them are.
   */
  const freeTierRows = [
    {
      key: "dataVolume",
      label: calculatorData?.tableLabels?.dataVolume || "Data Volume",
      amount: `${FREE_TIER.dataVolume}GB`,
      inUse: dataVolumeGB > 0,
    },
    {
      key: "rumSessions",
      label: calculatorData?.tableLabels?.rumSessions || "RUM Sessions",
      amount: FREE_TIER.rumSessions,
      inUse: rumSessions > 0,
    },
    {
      key: "syntheticChecks",
      label: calculatorData?.tableLabels?.syntheticChecks || "Synthetic Checks",
      amount: FREE_TIER.syntheticChecks,
      inUse: syntheticChecks > 0,
    },
    {
      key: "browserTestRuns",
      label: calculatorData?.tableLabels?.browserTestRuns || "Browser Test Runs",
      amount: FREE_TIER.browserTestRuns,
      inUse: browserTestRuns > 0,
    },
  ].filter((row) => row.inUse);

  const totalPrice =
      dataVolumeCost +
      rumSessionsCost +
      syntheticChecksCost +
      browserTestRunsCost +
      opsAiAnthropicCost;

  const handleInputChange = (e) => {
    setDataVolume(Number(e.target.value));
  };

  const handleUnitChange = (newUnit) => {
    setMemoryType(newUnit);
  };

  return (
      <div className="container" id="pricing-calculator">
        <div className="pricing-calculator">
          <div className={"calculate-label"}>
            {calculatorData?.title || "Calculate The Price Based On Your Usage"}
          </div>
          <div className="d-flex">
            <div className="left-block">
              <h4>{calculatorData?.subtitle || "Let's Find a Plan that Suits You"}</h4>

              <div className="data">
                <div className="data-flex">
                  <p>{calculatorData?.fields?.dataVolume || "Estimated Data Volume"}</p>
                  <div className="input-data gb">
                    <input
                        type="number"
                        value={dataVolume}
                        onChange={handleInputChange}
                    />
                    <SelectBox
                        selectedValue={memoryType}
                        items={[{ value: "GB" }, { value: "TB" }, { value: "PB" }]}
                        onChange={(e) => handleUnitChange(e.value)}
                    />
                  </div>
                </div>
                <RangeSlider
                    max={dataVolumeMax}
                    value={dataVolume}
                    onChange={(value) => setDataVolume(Number(value))}
                />
              </div>

              <div className="data">
                <div className="data-flex">
                  <p>{calculatorData?.fields?.rumSessions || "Estimated Real User Monitoring Sessions"}</p>
                  <div className="input-data">
                    <input
                        type="number"
                        value={rumSessions}
                        onChange={(e) => setRumSessions(Number(e.target.value))}
                    />
                  </div>
                </div>
                <RangeSlider
                    max={rumSessionsMax}
                    value={rumSessions}
                    onChange={(value) => setRumSessions(Number(value))}
                />
              </div>

              <div className="data">
                <div className="data-flex">
                  <p>{calculatorData?.fields?.syntheticChecks || "Estimated Synthetic Checks"}</p>
                  <div className="input-data">
                    <input
                        type="number"
                        value={syntheticChecks}
                        onChange={(e) => setSyntheticChecks(Number(e.target.value))}
                    />
                  </div>
                </div>
                <RangeSlider
                    max={syntheticChecksMax}
                    value={syntheticChecks}
                    onChange={(value) => setSyntheticChecks(Number(value))}
                />
              </div>

              <div className="data">
                <div className="data-flex">
                  <p>{calculatorData?.fields?.browserTestRuns || "Estimated Browser Test Runs"}</p>
                  <div className="input-data">
                    <input
                        type="number"
                        value={browserTestRuns}
                        onChange={(e) => setBrowserTestRuns(Number(e.target.value))}
                    />
                  </div>
                </div>
                <RangeSlider
                    max={browserTestRunsMax}
                    value={browserTestRuns}
                    onChange={(value) => setBrowserTestRuns(Number(value))}
                />
              </div>

              <div className="data">
                <div className="data-flex">
                  <p>{calculatorData?.fields?.opsAiTokenUsage || "OpsAI Token"}</p>
                  <div className="input-data">
                    <input
                        type="number"
                        value={opsAiIssues}
                        onChange={(e) => setOpsAiIssues(Number(e.target.value))}
                    />
                    
                  </div>
                </div>
                <RangeSlider
                    max={opsAiIssuesMax}
                    value={opsAiIssues}
                    onChange={(value) => setOpsAiIssues(Number(value))}
                />
              </div>

              <p className="info">
                {calculatorData?.info || "Data volume is the sum total of uncompressed logs, metrics, and traces."}
              </p>
              <p className="info mr-top-less">
                *Average token consumption per issue is for reference only and may vary based on issue type and complexity.
              </p>
            </div>

            <div className="right-block">
              <div className="inner-card">
                <div className="top-heading pd">
                  <h4 className="text-gradient">{calculatorData?.results?.congrats || "Congrats!"}</h4>
                  <p>
                    {calculatorData?.results?.consideringEstimation || "Considering your estimation, "}
                    {totalPrice > 0
                        ? (calculatorData?.results?.payAsYouGo || "Pay as you go")
                        : (calculatorData?.results?.freeForever || "Free Forever")}
                    {calculatorData?.results?.planFit || " plan will be a better fit for you."}
                  </p>
                </div>

                {freeTierRows.length > 0 && (
                  <div className="free-tier pd">
                    <div className="price-tag with-border">{calculatorData?.results?.freeTierUsage || "Free Tier Usage"}</div>
                    <table>
                      <tbody>
                      {freeTierRows.map((row) => (
                        <tr key={row.key}>
                          <td>{row.label}</td>
                          <td>{row.amount}</td>
                          <td>$0</td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="free-tier pd">
                  <div className="price-tag with-border">{calculatorData?.results?.additionalUsage || "Additional Usage"}</div>
                  <table>
                    <tbody>
                    <tr>
                      <td>{calculatorData?.tableLabels?.dataVolume || "Data Volume"}</td>
                      <td>{formatGigabytes(billableDataVolumeGB)}</td>
                      <td>${dataVolumeCost.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>{calculatorData?.tableLabels?.rumSessions || "RUM Sessions"}</td>
                      <td>{billableRumSessions}</td>
                      <td>${rumSessionsCost.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>{calculatorData?.tableLabels?.syntheticChecks || "Synthetic Checks"}</td>
                      <td>{billableSyntheticChecks}</td>
                      <td>${syntheticChecksCost.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>{calculatorData?.tableLabels?.browserTestRuns || "Browser Test Runs"}</td>
                      <td>{billableBrowserTestRuns}</td>
                      <td>${browserTestRunsCost.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td>{calculatorData?.tableLabels?.opsAiTokenUsage || "OpsAI Issue Fixes"}</td>
                      <td>
                        <Tooltip
                          position="top"
                          content={
                            <div className="anthropic-breakup-tooltip">
                              <p><strong>Token Breakup</strong></p>
                              <p><span>Input</span> <span>{formatTokenAmount(issueTokenBreakup.input)}</span></p>
                              <p><span>Output</span> <span>{formatTokenAmount(issueTokenBreakup.output)}</span></p>
                              <p><span>Cache</span> <span>{formatTokenAmount(issueTokenBreakup.cache)}</span></p>
                            </div>
                          }
                        >
                          <span className="opsai-tooltip-trigger">{formatTokenAmount(issueTokenTotal)}</span>
                        </Tooltip>
                      </td>
                      <td>
                        <Tooltip
                          position="top"
                          content={
                            <div className="anthropic-breakup-tooltip">
                              <p><strong>Cost Breakup</strong></p>
                              <p><span>Input</span> <span>${anthropicBreakup.input.toFixed(2)}</span></p>
                              <p><span>Output</span> <span>${anthropicBreakup.output.toFixed(2)}</span></p>
                              <p><span>Cache</span> <span>${anthropicBreakup.cache.toFixed(2)}</span></p>
                            </div>
                          }
                        >
                          <span className="opsai-tooltip-trigger">${opsAiAnthropicCost.toFixed(2)}</span>
                        </Tooltip>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <div className={"pricing-card-dots"}>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="total-price pd">
                  <p>{calculatorData?.results?.total || "Total"}</p>
                  <h3 className="text-gradient">${totalPrice.toFixed(2)}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default PricingCalculator;
